export interface Coordinates {
  x: number;
  y: number;
  z: number;
}

/**
 * Parses a string to extract three coordinate numbers.
 * It's flexible and will find the last three numbers in the string,
 * ignoring text, hyphens, and other symbols.
 * e.g., "minecraft:overworld -426 91 188" -> {x: -426, y: 91, z: 188}
 */
export const parseCoordinates = (str: string): Coordinates | null => {
  const numbers = str.match(/-?\d+(\.\d+)?/g);
  if (!numbers || numbers.length < 3) {
    return null;
  }
  const lastThree = numbers.slice(-3).map(Number);
  if (lastThree.some(isNaN)) {
    return null;
  }
  return {
    x: lastThree[0],
    y: lastThree[1],
    z: lastThree[2],
  };
};

/**
 * Parses a string to extract all numbers.
 * e.g., "minecraft:overworld -426 91 188" -> [-426, 91, 188]
 */
export const parseNumbers = (str: string): number[] => {
  const numbers = str.match(/-?\d+(\.\d+)?/g);
  if (!numbers) {
    return [];
  }
  return numbers.map(Number).filter(n => !isNaN(n));
};

/**
 * Generates all permutations of an array. Used for order-agnostic coord matching.
 */
const getPermutations = (arr: number[]): number[][] => {
  if (arr.length === 0) return [[]];
  const firstEl = arr[0];
  const rest = arr.slice(1);
  const permsWithoutFirst = getPermutations(rest);
  const allPermutations: number[][] = [];
  permsWithoutFirst.forEach(perm => {
    for (let i = 0; i < perm.length + 1; i++) {
      const permWithFirst = [...perm.slice(0, i), firstEl, ...perm.slice(i)];
      allPermutations.push(permWithFirst);
    }
  });
  return allPermutations;
};

/**
 * Fuzzy matches coordinate sets by checking all permutations of the warp's coords
 * to see if one is within a tolerance of the query's coordinates.
 * This is order-agnostic and supports partial queries (1, 2, or 3 numbers).
 * @returns The "difference score" of the best match (lower is better), or null if no match.
 */
export const fuzzyMatchPartialCoordinates = (
  queryNumbers: number[],
  warpCoords: Coordinates,
  tolerance: number = 100
): number | null => {
  const warpValues = [warpCoords.x, warpCoords.y, warpCoords.z];
  const permutations = getPermutations(warpValues);
  
  let bestDifference: number | null = null;

  for (const p of permutations) {
    // Take the first N elements of the permutation, where N is the number of coords in the query.
    const p_subset = p.slice(0, queryNumbers.length);
    
    let isMatch = true;
    let currentDifference = 0;

    for (let i = 0; i < queryNumbers.length; i++) {
      const diff = Math.abs(queryNumbers[i] - p_subset[i]);
      if (diff > tolerance) {
        isMatch = false;
        break;
      }
      currentDifference += diff;
    }

    if (isMatch) {
      if (bestDifference === null || currentDifference < bestDifference) {
        bestDifference = currentDifference;
      }
    }
  }

  return bestDifference;
};

/**
 * Calculates the Euclidean distance between two points in 3D space.
 */
export const calculateDistance = (pos1: Coordinates, pos2: Coordinates): number => {
  const dx = pos1.x - pos2.x;
  const dy = pos1.y - pos2.y;
  const dz = pos1.z - pos2.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};
